import React, { useState, useEffect } from 'react';
import Header from './components/Header';
import WorkoutSelector from './components/WorkoutSelector';
import WorkoutScreen from './components/WorkoutScreen';
import './App.css'; // We'll create this for global styles

const HEADER_ONLY_HEIGHT = '55px'; // Approximate height of header without timer
const HEADER_WITH_TIMER_CONTROLS_HEIGHT = '135px'; // Increased height for skip controls

function App() {
  const [selectedWorkout, setSelectedWorkout] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const [overallTimeLeft, setOverallTimeLeft] = useState(0);
  // overallTimeLeft is managed by the Timer component itself via its duration prop and onTick updates
  // We just need a key to reset it and its initial duration.
  const [timerKey, setTimerKey] = useState('overall-timer-init');
  const [actualCalculatedWorkoutDuration, setActualCalculatedWorkoutDuration] = useState(0);
  const [effectiveTotalDurationForWorkout, setEffectiveTotalDurationForWorkout] = useState(0);

  const resetTimer = (durationToUse) => {
    // Ensure durationToUse is a positive number before setting
    const validDuration = Math.max(0, durationToUse);
    setOverallTimeLeft(validDuration);
    setEffectiveTotalDurationForWorkout(validDuration); // Store the duration used for this timer session
    setTimerKey(`overall-timer-reset-${Date.now()}`); // More specific key
    setIsPaused(false);
  };

  useEffect(() => {
    // When a workout is selected OR the actual duration is calculated and it's different,
    // reset the timer with the best available duration.
    if (selectedWorkout) {
        // Prefer actualCalculatedWorkoutDuration if it has been set and is valid
        const duration = actualCalculatedWorkoutDuration > 0 ? actualCalculatedWorkoutDuration : selectedWorkout.totalDuration;
        resetTimer(duration);
    } else {
        setEffectiveTotalDurationForWorkout(0); // Clear when no workout selected
    }
  }, [selectedWorkout, actualCalculatedWorkoutDuration]); // Re-run when these change

  const handleSelectWorkout = (workout) => {
    setSelectedWorkout(workout);
    setActualCalculatedWorkoutDuration(0); // Reset actual duration until WorkoutScreen calculates it
    // The useEffect above will handle resetting the timer once selectedWorkout is set.
  };

  const handleActualDurationCalculated = (calculatedDuration) => {
    console.log("App.js: Actual duration calculated:", calculatedDuration);
    setActualCalculatedWorkoutDuration(calculatedDuration);
    // The useEffect listening to [selectedWorkout, actualCalculatedWorkoutDuration] will reset timer.
  };

  const handleBackToSelector = () => {
    if (selectedWorkout && !window.confirm("Are you sure you want to end the workout and go back to the menu?")) {
      return; // User cancelled
    }
    setSelectedWorkout(null);
    setActualCalculatedWorkoutDuration(0); // Clear actual duration
    setOverallTimeLeft(0); // Clear timer display
  };

  const handleOverallTimerTick = (newTimeLeft) => {
    setOverallTimeLeft(newTimeLeft);
  };

  const handleOverallTimerComplete = () => {
    console.log("App.js: Overall workout timer completed!");
    setIsPaused(true);
  };

  const handleTimeManipulation = (newTime) => {
    const validatedTime = Math.max(0, Math.min(effectiveTotalDurationForWorkout, newTime));
    setOverallTimeLeft(validatedTime);
    setTimerKey(`overall-timer-manipulate-${Date.now()}`);
    if (validatedTime === 0 && !isPaused) {
        setIsPaused(true);
    }
    if (validatedTime > 0 && isPaused && newTime > overallTimeLeft) {
        // If skipping forward into a playing state, consider unpausing, but risky without context
        // For now, manual resume is better after a skip/teleport if it was paused.
    }
  };

  const handleSkipTime = (seconds) => {
    handleTimeManipulation(overallTimeLeft + seconds);
  };

  const handleSetOverallTimeLeft = (newTime) => { // New handler for direct set
    handleTimeManipulation(newTime);
  };
  
  // Apply padding to the entire App container to prevent content from being hidden by fixed Header
  const appStyle = {
    paddingTop: selectedWorkout ? HEADER_WITH_TIMER_CONTROLS_HEIGHT : HEADER_ONLY_HEIGHT,
  };

  return (
    <div className="App" style={appStyle}>
      <Header
        title="Mom's Workouts"
        showBackButton={!!selectedWorkout}
        onBackClick={handleBackToSelector}
        showTimerArea={!!selectedWorkout}
        timerKey={timerKey}
        timerCurrentDuration={overallTimeLeft}
        isTimerPaused={isPaused}
        onTimerPause={() => setIsPaused(true)}
        onTimerResume={() => setIsPaused(false)}
        onTimerTick={handleOverallTimerTick}
        onTimerComplete={handleOverallTimerComplete}
        onSkipTime={handleSkipTime}
      />
      <main style={styles.mainContent}> {/* mainContent style might not need its own paddingTop now */}
        {!selectedWorkout ? (
          <WorkoutSelector onSelectWorkout={handleSelectWorkout} />
        ) : (
          <WorkoutScreen
            workout={selectedWorkout}
            onBack={handleBackToSelector}
            overallTimeLeftProp={overallTimeLeft}
            effectiveTotalDurationProp={effectiveTotalDurationForWorkout}
            isOverallPaused={isPaused}
            setOverallIsPaused={setIsPaused}
            onActualDurationCalculated={handleActualDurationCalculated}
            onSetOverallTimeLeft={handleSetOverallTimeLeft} // Pass new handler
          />
        )}
      </main>
      <footer style={styles.footer}>
        <p>&copy; {new Date().getFullYear()} Mom's Workout Planner. Stay Active!</p>
      </footer>
    </div>
  );
}

const styles = {
  mainContent: {
    // paddingTop: '0 0 60px 0', // Padding is now handled by the .App style based on header height
    flexGrow: 1,
    paddingBottom: '60px', // Keep bottom padding for footer
  },
  footer: {
    backgroundColor: '#f0f0f0',
    textAlign: 'center',
    padding: '15px',
    borderTop: '1px solid #ddd',
    fontSize: '0.9em',
    color: '#555',
  }
}

export default App;
